import dayjs from 'dayjs';
import isNumber from 'lodash/isNumber';
import mergeWith from 'lodash/mergeWith';
import reduce from 'lodash/reduce';

import { HOURS_IN_DAY } from '@cast/constants';
import { PriceType } from '@cast/shared-types';

import { CostReportDataByPeriod } from '../../types/costOverTime';
import {
  getUptimeMultiplier,
  hasCpuAndNotForecastedDataPoint,
  hasFallbackDataPoint,
  hasGpuAndNotForecastedDataPoint,
  isPastDataPoint,
} from '../costOverTime';

type AverageCostMetrics = {
  totalCost: number;
  onDemandCost: number;
  fallbackCost: number;
  spotCost: number;
  onDemandCpuCount: number;
  fallbackCpuCount: number;
  spotCpuCount: number;
  onDemandGpuCount: number;
  fallbackGpuCount: number;
  spotGpuCount: number;
  onDemandTpuCount: number;
  fallbackTpuCount: number;
  spotTpuCount: number;
  onDemandPodCount: number;
  fallbackPodCount: number;
  spotPodCount: number;
  normalizedCostPerCpu: number;
  normalizedCostPerGpu: number;
  normalizedCostPerTpu: number;
  normalizedCostPerGib: number;
  normalizedCostPerPod: number;
  onDemandCostPerCpu: number;
  onDemandCostPerGpu: number;
  onDemandCostPerTpu: number;
  onDemandCostPerPod: number;
  onDemandCostPerGib: number;
  fallbackCostPerCpu: number;
  fallbackCostPerGpu: number;
  fallbackCostPerTpu: number;
  fallbackCostPerGib: number;
  fallbackCostPerPod: number;
  spotCostPerCpu: number;
  spotCostPerGpu: number;
  spotCostPerTpu: number;
  spotCostPerGib: number;
  spotCostPerPod: number;
  totalStorage: number;
  totalStorageCost: number;
  costPerStorage: number;
  totalCpu: number;
  totalCpuCost: number;
  totalGpu: number;
  totalGpuCost: number;
  totalTpu: number;
  totalTpuCost: number;
  totalRamCost: number;
  totalRamGib: number;
  fallbackCpuCost: number;
  fallbackGpuCost: number;
  fallbackTpuCost: number;
  fallbackRamCost: number;
  fallbackRamGib: number;
  fallbackUptime: number;
  normalizedCostPerStorage: number;
  onDemandCpuCost: number;
  onDemandGpuCost: number;
  onDemandTpuCost: number;
  onDemandRamCost: number;
  onDemandRamGib: number;
  onDemandUptime: number;
  spotCpuCost: number;
  spotGpuCost: number;
  spotTpuCost: number;
  spotRamCost: number;
  spotRamGib: number;
  spotUptime: number;
};

const emptyMetrics: AverageCostMetrics = {
  totalCost: 0,
  onDemandCost: 0,
  fallbackCost: 0,
  spotCost: 0,
  onDemandCpuCount: 0,
  fallbackCpuCount: 0,
  spotCpuCount: 0,
  onDemandGpuCount: 0,
  fallbackGpuCount: 0,
  spotGpuCount: 0,
  onDemandTpuCount: 0,
  fallbackTpuCount: 0,
  spotTpuCount: 0,
  onDemandPodCount: 0,
  fallbackPodCount: 0,
  spotPodCount: 0,
  normalizedCostPerCpu: 0,
  normalizedCostPerGpu: 0,
  normalizedCostPerTpu: 0,
  normalizedCostPerGib: 0,
  normalizedCostPerPod: 0,
  onDemandCostPerCpu: 0,
  onDemandCostPerGpu: 0,
  onDemandCostPerTpu: 0,
  onDemandCostPerPod: 0,
  onDemandCostPerGib: 0,
  fallbackCostPerCpu: 0,
  fallbackCostPerGpu: 0,
  fallbackCostPerTpu: 0,
  fallbackCostPerGib: 0,
  fallbackCostPerPod: 0,
  spotCostPerCpu: 0,
  spotCostPerGpu: 0,
  spotCostPerTpu: 0,
  spotCostPerGib: 0,
  spotCostPerPod: 0,
  totalStorage: 0,
  totalStorageCost: 0,
  costPerStorage: 0,
  totalCpu: 0,
  totalCpuCost: 0,
  totalGpu: 0,
  totalGpuCost: 0,
  totalTpu: 0,
  totalTpuCost: 0,
  totalRamCost: 0,
  totalRamGib: 0,
  fallbackCpuCost: 0,
  fallbackGpuCost: 0,
  fallbackTpuCost: 0,
  fallbackRamCost: 0,
  fallbackRamGib: 0,
  fallbackUptime: 0,
  normalizedCostPerStorage: 0,
  onDemandCpuCost: 0,
  onDemandGpuCost: 0,
  onDemandTpuCost: 0,
  onDemandRamCost: 0,
  onDemandRamGib: 0,
  onDemandUptime: 0,
  spotCpuCost: 0,
  spotGpuCost: 0,
  spotTpuCost: 0,
  spotRamCost: 0,
  spotRamGib: 0,
  spotUptime: 0,
};

export function getAverageRates(
  dataPoints: CostReportDataByPeriod[],
  fallbackDataPoints: CostReportDataByPeriod[]
): AverageCostMetrics {
  const data: AverageCostMetrics = {
    ...emptyMetrics,
  };

  if (!dataPoints.length) {
    return data;
  }

  // Sum all the raw values from data points
  const dataPointsSum: AverageCostMetrics = reduce(
    dataPoints,
    (acc, value) =>
      mergeWith(acc, value, (dstValue: any, srcValue: any) => {
        if (isNumber(dstValue) && isNumber(srcValue)) {
          return dstValue + srcValue;
        }
        return undefined;
      }),
    data
  );

  // Define which metrics should use fallback data points length for averaging
  const fallbackMetrics: (keyof typeof dataPointsSum)[] = [
    'fallbackCost',
    'fallbackPodCount',
    'fallbackCostPerCpu',
    'fallbackCostPerGpu',
    'fallbackCostPerTpu',
    'fallbackCostPerGib',
    'fallbackCostPerPod',
    'fallbackCpuCount',
    'fallbackGpuCount',
    'fallbackTpuCount',
    'fallbackCpuCost',
    'fallbackGpuCost',
    'fallbackTpuCost',
    'fallbackRamCost',
    'fallbackRamGib',
    'fallbackUptime',
  ];

  // Calculate averages for all summed values
  const averagedData = reduce(
    Object.keys(dataPointsSum),
    (acc, key) => {
      const value = dataPointsSum[key as keyof typeof dataPointsSum];
      const divisor = fallbackMetrics.includes(
        key as keyof typeof dataPointsSum
      )
        ? fallbackDataPoints?.length || 0
        : dataPoints.length;
      return {
        ...acc,
        [key]: value !== undefined && divisor !== 0 ? value / divisor : 0,
      };
    },
    {} as AverageCostMetrics
  );

  // Calculate per-resource costs based on averaged totals
  return {
    ...averagedData,
    normalizedCostPerCpu:
      averagedData.totalCpu > 0
        ? averagedData.totalCpuCost / averagedData.totalCpu
        : 0,

    normalizedCostPerGpu:
      averagedData.totalGpu > 0
        ? averagedData.totalGpuCost / averagedData.totalGpu
        : 0,

    normalizedCostPerTpu:
      averagedData.totalTpu > 0
        ? averagedData.totalTpuCost / averagedData.totalTpu
        : 0,

    normalizedCostPerGib:
      averagedData.totalRamGib > 0
        ? averagedData.totalRamCost / averagedData.totalRamGib
        : 0,

    normalizedCostPerStorage:
      averagedData.totalStorage > 0
        ? averagedData.totalStorageCost / averagedData.totalStorage
        : 0,

    onDemandCostPerCpu:
      averagedData.onDemandCpuCount > 0
        ? averagedData.onDemandCpuCost / averagedData.onDemandCpuCount
        : 0,

    onDemandCostPerGpu:
      averagedData.onDemandGpuCount > 0
        ? averagedData.onDemandGpuCost / averagedData.onDemandGpuCount
        : 0,

    onDemandCostPerTpu:
      averagedData.onDemandTpuCount > 0
        ? averagedData.onDemandTpuCost / averagedData.onDemandTpuCount
        : 0,

    onDemandCostPerGib:
      averagedData.onDemandRamGib > 0
        ? averagedData.onDemandRamCost / averagedData.onDemandRamGib
        : 0,

    fallbackCostPerCpu:
      averagedData.fallbackCpuCount > 0
        ? averagedData.fallbackCpuCost / averagedData.fallbackCpuCount
        : 0,

    fallbackCostPerGpu:
      averagedData.fallbackGpuCount > 0
        ? averagedData.fallbackGpuCost / averagedData.fallbackGpuCount
        : 0,

    fallbackCostPerTpu:
      averagedData.fallbackTpuCount > 0
        ? averagedData.fallbackTpuCost / averagedData.fallbackTpuCount
        : 0,

    fallbackCostPerGib:
      averagedData.fallbackRamGib > 0
        ? averagedData.fallbackRamCost / averagedData.fallbackRamGib
        : 0,

    spotCostPerCpu:
      averagedData.spotCpuCount > 0
        ? averagedData.spotCpuCost / averagedData.spotCpuCount
        : 0,

    spotCostPerGpu:
      averagedData.spotGpuCount > 0
        ? averagedData.spotGpuCost / averagedData.spotGpuCount
        : 0,

    spotCostPerTpu:
      averagedData.spotTpuCount > 0
        ? averagedData.spotTpuCost / averagedData.spotTpuCount
        : 0,

    spotCostPerGib:
      averagedData.spotRamGib > 0
        ? averagedData.spotRamCost / averagedData.spotRamGib
        : 0,

    costPerStorage:
      averagedData.totalStorage > 0
        ? averagedData.totalStorageCost / averagedData.totalStorage
        : 0,
  };
}

export const isCurrentDayDatapoint = (
  { timestamp, totalCpu, forecast }: CostReportDataByPeriod,
  timezone: string
): boolean =>
  !!totalCpu && !forecast && dayjs.utc(timestamp).tz(timezone).isToday();

type UptimeCostMetrics = {
  total: number;
  onDemand: number;
  fallback: number;
  spot: number;
  storage: number;
};

const getUptimeCost = (
  dataPoints: CostReportDataByPeriod[],
  storageCostGranularity: PriceType
): UptimeCostMetrics => {
  let data: UptimeCostMetrics = {
    total: 0,
    onDemand: 0,
    fallback: 0,
    spot: 0,
    storage: 0,
  };

  if (dataPoints.length) {
    data = dataPoints.reduce((acc, datapoint) => {
      const multiplier = getUptimeMultiplier(datapoint);

      acc.onDemand += datapoint.onDemandCost * multiplier.onDemand;
      acc.fallback += datapoint.fallbackCost * multiplier.fallback;
      acc.spot += datapoint.spotCost * multiplier.spot;
      acc.storage += datapoint.totalStorageCost * multiplier.storage;
      return acc;
    }, data);

    data.storage =
      storageCostGranularity === PriceType.HOURLY
        ? data.storage * HOURS_IN_DAY
        : data.storage;
    data.total = data.onDemand + data.fallback + data.spot + data.storage;
  }

  return data;
};

export const aggregateCostOverTimeMetrics = (
  data: CostReportDataByPeriod[],
  timezone: string,
  storageCostGranularity: PriceType
): {
  total: {
    cost: number;
    cpu: number;
    gpu: number;
    pods: number;
    onDemand: number;
    onDemandPods: number;
    fallback: number;
    fallbackPods: number;
    spot: number;
    spotPods: number;
    storage: number;
  };
  ongoing: AverageCostMetrics;
  average: AverageCostMetrics;
  uptimeTotal: UptimeCostMetrics;
  uptimeOngoing: UptimeCostMetrics;
} => {
  let partitionedDataPoints: Record<string, CostReportDataByPeriod[]> = {
    total: [],
    today: [],
    todayFallback: [],
    genuine: [],
    genuineFallback: [],
  };

  partitionedDataPoints = data.reduce((acc, datapoint) => {
    if (
      hasCpuAndNotForecastedDataPoint(datapoint) ||
      hasGpuAndNotForecastedDataPoint(datapoint)
    ) {
      acc.genuine.push(datapoint);

      if (isPastDataPoint(datapoint.timestamp, timezone)) {
        acc.total.push(datapoint);
      }

      if (hasFallbackDataPoint(datapoint)) {
        acc.genuineFallback.push(datapoint);
      }
    }

    if (isCurrentDayDatapoint(datapoint, timezone)) {
      acc.today.push(datapoint);

      if (hasFallbackDataPoint(datapoint)) {
        acc.todayFallback.push(datapoint);
      }
    }

    return acc;
  }, partitionedDataPoints);

  let aggregate = {
    total: {
      cost: 0,
      cpu: 0,
      gpu: 0,
      pods: 0,
      onDemand: 0,
      onDemandPods: 0,
      fallback: 0,
      fallbackPods: 0,
      spot: 0,
      spotPods: 0,
      storage: 0,
    },
    ongoing: {
      ...emptyMetrics,
    },
    average: {
      ...emptyMetrics,
    },
    uptimeTotal: {
      total: 0,
      onDemand: 0,
      fallback: 0,
      spot: 0,
      storage: 0,
    },
    uptimeOngoing: {
      total: 0,
      onDemand: 0,
      fallback: 0,
      spot: 0,
      storage: 0,
    },
  };

  aggregate = partitionedDataPoints.total.reduce((acc, datapoint) => {
    acc.total.cost += datapoint.totalCost;
    acc.total.cpu += datapoint.totalCpu;
    acc.total.gpu += datapoint.totalGpu;
    acc.total.onDemand += datapoint.onDemandCost;
    acc.total.fallback += datapoint.fallbackCost;
    acc.total.spot += datapoint.spotCost;
    acc.total.storage += datapoint.totalStorageCost;

    if ('totalPods' in datapoint) {
      acc.total.pods += datapoint.totalPods;
    }

    return acc;
  }, aggregate);

  aggregate.ongoing = getAverageRates(
    partitionedDataPoints.today,
    partitionedDataPoints.todayFallback
  );
  aggregate.average = getAverageRates(
    partitionedDataPoints.genuine,
    partitionedDataPoints.genuineFallback
  );

  aggregate.uptimeTotal = getUptimeCost(
    partitionedDataPoints.genuine,
    storageCostGranularity
  );
  aggregate.uptimeOngoing = getUptimeCost(
    partitionedDataPoints.today,
    storageCostGranularity
  );

  return aggregate;
};
